import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { DashboardViewer, Status } from 'utils/customTypes';
import { RootState, ThunkApi } from 'state/store';
import { SLICE_STATUS } from 'utils/constants';
import { DASHBOARDS_LIST_ACTIONS } from 'utils/constants/dashboard';

/* ============================== STATE SETUP =============================== */

interface DashboardViewersState {
  status: Status;
  shareStatus: Status;
  deleteStatus: Status;
}

const initialState: DashboardViewersState = {
  status: SLICE_STATUS.IDLE,
  shareStatus: SLICE_STATUS.IDLE,
  deleteStatus: SLICE_STATUS.IDLE,
};

const dashboardViewersAdapter = createEntityAdapter<DashboardViewer>();

/* ============================== REDUX THUNK =============================== */

export const shareDashboard = createAsyncThunk<
  { dashboardId: string; viewerIds: string[] },
  { dashboardId: string; userIds: string[] },
  ThunkApi
>(
  'normalized/dashboardViewers/shareDashboard',
  async (
    { dashboardId, userIds },
    { getState, extra: { getDashboardApi } }
  ) => {
    const affordances =
      getState().normalized.dashboard.affordances.resources[dashboardId];
    const shareUrl = affordances?.[DASHBOARDS_LIST_ACTIONS.SHARE];
    if (!shareUrl) {
      throw new Error(
        `Missing share affordance URL for dashboard ${dashboardId}`
      );
    }
    const viewerIds = await getDashboardApi().shareDashboard(shareUrl, userIds);
    return { dashboardId, viewerIds };
  }
);

export const deleteViewer = createAsyncThunk<
  { dashboardId: string; viewerId: string },
  { dashboardId: string; viewerId: string },
  ThunkApi
>(
  'normalized/dashboardViewers/deleteViewer',
  async (
    { dashboardId, viewerId },
    { getState, extra: { getDashboardApi } }
  ) => {
    const affordances =
      getState().normalized.dashboard.affordances.resources[dashboardId];
    const shareUrl = affordances?.[DASHBOARDS_LIST_ACTIONS.SHARE];
    if (!shareUrl) {
      throw new Error(
        `Missing share affordance URL for dashboard ${dashboardId}`
      );
    }
    await getDashboardApi().deleteViewer(shareUrl, viewerId);
    return { dashboardId, viewerId };
  }
);

export const getDashboardViewers = createAsyncThunk<
  DashboardViewer[],
  void,
  ThunkApi
>(
  'normalized/dashboardViewers/getViewers',
  async (_, { extra: { getDashboardApi } }) => {
    return await getDashboardApi().getViewers();
  }
);

/* ================================= REDUCER ================================ */

const dashboardViewersSlice = createSlice({
  name: 'normalized/dashboardViewers',
  initialState: dashboardViewersAdapter.getInitialState(initialState),
  reducers: {
    clearDashboardViewers: (state) => {
      dashboardViewersAdapter.removeAll(state);
      state.status = SLICE_STATUS.IDLE;
      state.shareStatus = SLICE_STATUS.IDLE;
      state.deleteStatus = SLICE_STATUS.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardViewers.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getDashboardViewers.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getDashboardViewers.fulfilled, (state, action) => {
        dashboardViewersAdapter.setAll(state, action.payload);
        state.status = SLICE_STATUS.SUCCESS;
      })
      .addCase(shareDashboard.pending, (state) => {
        state.shareStatus = SLICE_STATUS.LOADING;
      })
      .addCase(shareDashboard.rejected, (state) => {
        state.shareStatus = SLICE_STATUS.FAILED;
      })
      .addCase(deleteViewer.pending, (state) => {
        state.deleteStatus = SLICE_STATUS.LOADING;
      })
      .addCase(deleteViewer.rejected, (state) => {
        state.deleteStatus = SLICE_STATUS.FAILED;
      })
      .addCase(deleteViewer.fulfilled, (state, action) => {
        state.deleteStatus = SLICE_STATUS.SUCCESS;
        const { dashboardId, viewerId } = action.payload;
        const viewer = state.entities[viewerId];
        if (viewer) {
          dashboardViewersAdapter.updateOne(state, {
            id: viewerId,
            changes: {
              sharedDashboardIds: viewer.sharedDashboardIds.filter(
                (id) => id !== dashboardId
              ),
            },
          });
        }
      })
      .addCase(shareDashboard.fulfilled, (state, action) => {
        state.shareStatus = SLICE_STATUS.SUCCESS;
        const { dashboardId, viewerIds } = action.payload;
        const updates = viewerIds
          .filter((viewerId) => {
            const viewer = state.entities[viewerId];
            return viewer && !viewer.sharedDashboardIds.includes(dashboardId);
          })
          .map((viewerId) => ({
            id: viewerId,
            changes: {
              sharedDashboardIds: [
                ...state.entities[viewerId]!.sharedDashboardIds,
                dashboardId,
              ],
            },
          }));
        if (updates.length > 0) {
          dashboardViewersAdapter.updateMany(state, updates);
        }
      });
  },
});

/* ================================ ACTIONS ================================= */

export const { clearDashboardViewers } = dashboardViewersSlice.actions;

/* =============================== SELECTORS ================================ */

const defaultSelectors = dashboardViewersAdapter.getSelectors<RootState>(
  (state) => state.normalized.dashboardViewers
);

const selectDashboardViewersStatus = (state: RootState) =>
  state.normalized.dashboardViewers.status;

const selectShareStatus = (state: RootState) =>
  state.normalized.dashboardViewers.shareStatus;

const selectDeleteStatus = (state: RootState) =>
  state.normalized.dashboardViewers.deleteStatus;

const selectViewersByDashboardId = (dashboardId: string) =>
  createSelector([defaultSelectors.selectAll], (allViewers) => {
    const dashboardViewers = allViewers.filter((v) =>
      v.sharedDashboardIds.includes(dashboardId)
    );
    const availableViewers = allViewers.filter(
      (v) => !v.sharedDashboardIds.includes(dashboardId)
    );
    return { dashboardViewers, availableViewers };
  });

export const dashboardViewersSelectors = {
  ...defaultSelectors,
  selectDashboardViewersStatus,
  selectShareStatus,
  selectDeleteStatus,
  selectViewersByDashboardId,
};

export default dashboardViewersSlice.reducer;
